import { useState, useRef, useCallback } from 'react';

const DEFAULT_SWIPE_THRESHOLD = 40;

/**
 * Shared photo-gallery index + swipe logic used by both the catalog CarCard and
 * the VDP main photo. Keeps the wrap-around index math and the touch-swipe
 * handling in one place so the two galleries can't drift apart.
 *
 * @param {number} count - number of photos
 * @param {object} [opts]
 * @param {number} [opts.swipeThreshold=40] - min horizontal px to count as a swipe
 * @returns {{
 *   index: number,
 *   setIndex: (i: number) => void,
 *   next: () => void,
 *   prev: () => void,
 *   didSwipeRef: import('react').MutableRefObject<boolean>,
 *   swipeHandlers: { onTouchStart: Function, onTouchEnd: Function },
 * }}
 *
 * `didSwipeRef` is set true right after a horizontal swipe so callers whose
 * photo also has an onClick (CarCard navigates to the VDP) can swallow the
 * click the browser synthesises after the drag. Callers without such a click
 * (the VDP) can just ignore it.
 */
export function usePhotoGallery(count, { swipeThreshold = DEFAULT_SWIPE_THRESHOLD } = {}) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const didSwipeRef = useRef(false);

  const next = useCallback(() => {
    setIndex((i) => (count < 1 ? 0 : (i + 1) % count));
  }, [count]);

  const prev = useCallback(() => {
    setIndex((i) => (count < 1 ? 0 : (i - 1 + count) % count));
  }, [count]);

  const onTouchStart = useCallback((e) => {
    const t = e.changedTouches[0];
    touchStartX.current = t.clientX;
    touchStartY.current = t.clientY;
    didSwipeRef.current = false;
  }, []);

  const onTouchEnd = useCallback((e) => {
    if (touchStartX.current === null) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStartX.current;
    const dy = t.clientY - touchStartY.current;
    touchStartX.current = null;
    touchStartY.current = null;
    // Horizontal intent only (ignore vertical scroll gestures) + min distance.
    if (count < 2 || Math.abs(dx) < swipeThreshold || Math.abs(dx) < Math.abs(dy)) return;
    didSwipeRef.current = true;
    setIndex((i) => (dx < 0 ? (i + 1) % count : (i - 1 + count) % count));
  }, [count, swipeThreshold]);

  return {
    index,
    setIndex,
    next,
    prev,
    didSwipeRef,
    swipeHandlers: { onTouchStart, onTouchEnd },
  };
}

export default usePhotoGallery;
